import {
    AppState,
    getActiveLeftNavItemId,
    GroupsHubListLoadingState,
    UnifiedGroupsMap,
} from '../../../appstate';
import {
    ownedUnifiedGroupsLeftNavId,
    joinedUnifiedGroupsLeftNavId,
    deletedUnifiedGroupsLeftNavId,
    createSelector,
    HubListEntryKind,
    UnifiedGroup,
} from '../../../shared';
import type {
    UnifiedGroupSorting,
    UnifiedGroupFilter,
} from '../../models/unifiedGroupListViewState';
import type { HubState } from '../../state';
import type { GroupsHubListLoadingViewState } from '../../state/groupsState';
import { makeGroupedEntries } from '../../selectors/groups/unifiedGroupListSelectorUtils';
import DisplayOrder from 'owa-people-types/lib/types/DisplayOrder';

export type UnifiedGroupEntry = UnifiedGroup & { kind: HubListEntryKind.Content };

export type GroupedUnifiedGroupEntries = {
    readonly kind: HubListEntryKind.Group;
    readonly index: string;
    readonly iconName?: string;
};

export type UnifiedGroupListEntry = GroupedUnifiedGroupEntries | UnifiedGroupEntry;

/*
 * Returns the content of the unifiedGroupList, sorted and grouped
 */
export const getGroupedUnifiedGroupListEntries = createSelector<
    AppState,
    HubState,
    UnifiedGroupsMap,
    UnifiedGroupFilter,
    UnifiedGroupSorting,
    string,
    string,
    string[],
    string[],
    string[],
    UnifiedGroupListEntry[]
>(
    'getGroupedUnifiedGroupListEntries',
    [
        (appState: AppState) => appState.data.groupsHubState.unifiedGroupsMap,
        (_appState: AppState, hubState: HubState) =>
            hubState?.view && hubState.view.groupsState.unifiedGroupListViewState.filter,
        (_appState: AppState, hubState: HubState) =>
            hubState?.view && hubState.view.groupsState.unifiedGroupListViewState.sorting,
        (appState: AppState) => appState.view.navigationState.clickedLeftNavItemId,
        (appState: AppState) => appState.data.userConfiguration.settings.UserCulture,
        (appState: AppState) => appState.data.groupsHubState.joinedGroupsIds,
        (appState: AppState) => appState.data.groupsHubState.ownedGroupsIds,
        (appState: AppState) => appState.data.groupsHubState.deletedGroupsIds,
    ],
    (
        unifiedGroups,
        filter,
        sortOrder,
        activeLeftNavItemId,
        userCulture,
        joinedGroupsIds,
        ownedGroupsIds,
        deletedGroupsIds
    ) => {
        if (!unifiedGroups) {
            return [];
        }

        let entries: UnifiedGroup[] = [];
        if (activeLeftNavItemId === joinedUnifiedGroupsLeftNavId) {
            entries = joinedGroupsIds.map(key => unifiedGroups[key]);
        } else if (activeLeftNavItemId === ownedUnifiedGroupsLeftNavId) {
            entries = ownedGroupsIds.map(key => unifiedGroups[key]);
        } else if (activeLeftNavItemId === deletedUnifiedGroupsLeftNavId) {
            entries = deletedGroupsIds.map(key => unifiedGroups[key]);
        }
        return makeGroupedEntries(
            entries,
            filter,
            sortOrder,
            DisplayOrder.FirstNameLastName,
            activeLeftNavItemId,
            userCulture
        );
    }
);

export function toUnifiedGroupListElement(persona: UnifiedGroup): UnifiedGroupEntry {
    return {
        kind: HubListEntryKind.Content,
        ...persona,
    };
}

/**
 * Returns a value indicating whether to display results, a loading message or an error message
 */
export const getLoadingState = createSelector<
    AppState,
    HubState,
    UnifiedGroupListEntry[],
    string,
    GroupsHubListLoadingState,
    GroupsHubListLoadingViewState
>(
    'getLoadingState',
    [
        getGroupedUnifiedGroupListEntries,
        getActiveLeftNavItemId,
        (appState: AppState) =>
            appState.data.groupsHubState.prefetchState.unifiedGroupListLoadingState,
    ],
    (unifiedGroupListEntries, activeLeftNavItemId, groupsHubLoadingState) => {
        switch (groupsHubLoadingState) {
            case 'Loading':
                return 'loading';
            case 'Done':
                return unifiedGroupListEntries && unifiedGroupListEntries.length > 0
                    ? 'results'
                    : 'empty';
            case 'Error':
            default:
                return 'error';
        }
    }
);

/*
 * Returns the first item from the groups list
 */
export const getFirstUnifiedGroupIdInList = createSelector<
    AppState,
    HubState,
    GroupsHubListLoadingViewState,
    UnifiedGroupListEntry[],
    string
>(
    'getFirstUnifiedGroupIdInList',
    [getLoadingState, getGroupedUnifiedGroupListEntries],
    (loadingState, unifiedGroupListEntries) => {
        if (!unifiedGroupListEntries || loadingState !== 'results') {
            return null;
        }
        const groupListElements = unifiedGroupListEntries.filter(
            entry => entry.kind === HubListEntryKind.Content
        ) as UnifiedGroupEntry[];

        if (groupListElements.length > 0) {
            return groupListElements[0].hubPersonaId;
        }

        return null;
    }
);
